import React, { useCallback, useMemo, useState, FC } from 'react';
import dynamic from 'next/dynamic';
import { styled } from '@mui/material';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import { enqueueSnackbar } from 'notistack';
import { useRequest } from 'ahooks';
import { observer } from 'mobx-react-lite';
import bs58 from 'bs58';
import { Connection } from '@solana/web3.js';
import { useStoreOrNull } from '@/stores/StoreProvider';
import WalletSubHeader from '@/components/WalletSubHeader';
import {
	buildAuthMessage,
	getDeposits,
	getIdentity,
	getRedeems,
	type FeeAuth,
} from '@/api/feeDashboard';
import FeePositions from '@/components/ReferrerDashboard/FeePositions';
import FeeHistory from '@/components/ReferrerDashboard/FeeHistory';
import { Card, Muted, Root } from '@/components/ReferrerDashboard/styles';

const PAGE_SIZE = 20;

// The server rejects a signature whose timestamp is outside its skew window.
// Re-sign a little before that so a long session does not start 401-ing
// mid-scroll; the exact server value is not exposed, and 5 minutes is the
// production setting.
const SIGNATURE_TTL_MS = 4 * 60 * 1000;

const Header = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	marginBottom: 24,
});

const SummaryGrid = styled(Box)({
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
	gap: 12,
	width: '100%',
	marginBottom: 24,
});

const Stat = styled(Card)({
	padding: '12px 16px',
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
});

const ReferrerDashboard: FC = observer(() => {
	const store = useStoreOrNull();
	const [auth, setAuth] = useState<FeeAuth | null>(null);
	const [signing, setSigning] = useState(false);
	const [tab, setTab] = useState<'positions' | 'redeems' | 'deposits'>('positions');

	const evmWallet = store?.wallets.connectedWallets.find((w) => w.type === 'evm');
	const solanaWallet = store?.wallets.connectedWallets.find((w) => w.type === 'solana');
	const connectedAddress = evmWallet?.selectedAddress ?? solanaWallet?.selectedAddress;

	const solanaConnection = useMemo(() => {
		const rpc = process.env.NEXT_PUBLIC_SOLANA_RPC;
		if (rpc) {
			return new Connection(rpc, 'confirmed');
		}
		return store?.wallets.svm.connections.get(0) ?? null;
	}, [store]);

	// Dropping the signature when the wallet changes prevents showing one
	// wallet's fees under another's address after a switch.
	const authValid =
		auth &&
		auth.address.toLowerCase() === (connectedAddress ?? '').toLowerCase() &&
		Date.now() - auth.timestamp * 1000 < SIGNATURE_TTL_MS;
	const activeAuth = authValid ? auth : null;

	const signIn = useCallback(async () => {
		if (!connectedAddress) {
			enqueueSnackbar('Connect a wallet first', { variant: 'warning' });
			return;
		}
		setSigning(true);
		try {
			const timestamp = Math.floor(Date.now() / 1000);
			const message = buildAuthMessage(connectedAddress, timestamp);
			let signature: string;
			if (evmWallet?.isEVM()) {
				const signer = evmWallet.signer;
				if (!signer) throw new Error('Signer not available');
				// EIP-191 personal_sign; the server recovers the address from it.
				signature = await signer.signMessage(message);
			} else {
				const wallet = solanaWallet as unknown as { signMessage(m: Uint8Array): Promise<Uint8Array> };
				if (!wallet?.signMessage) throw new Error('This wallet cannot sign messages');
				// Raw ed25519 over the UTF-8 bytes; the API takes base58 or hex.
				signature = bs58.encode(await wallet.signMessage(new TextEncoder().encode(message)));
			}
			setAuth({ address: connectedAddress, signature, timestamp, kind: evmWallet ? 'evm' : 'solana' });
		} catch (err: any) {
			enqueueSnackbar(err?.message || 'Signature rejected', { variant: 'error' });
		} finally {
			setSigning(false);
		}
	}, [connectedAddress, evmWallet, solanaWallet]);

	const { data: identity, loading: identityLoading } = useRequest(
		async () => (activeAuth ? getIdentity(activeAuth) : null),
		{ refreshDeps: [activeAuth?.signature], ready: Boolean(activeAuth) },
	);

	const totalBps = useMemo(
		() => (identity?.memberships ?? []).reduce((sum, m) => sum + m.bps, 0),
		[identity],
	);

	return (
		<Root>
			<WalletSubHeader vms={['evm', 'solana']} />

			<Header>
				<Typography variant="numsBig" sx={{ mb: 1, mt: 2 }}>
					Referrer Fees
				</Typography>
				<Muted variant="appSmallRegular" sx={{ textAlign: 'center' }}>
					Your fee balances across every chain, what has been paid out, and why anything is not being
					redeemed.
				</Muted>
			</Header>

			{!connectedAddress && (
				<Muted variant="appSmallRegular" sx={{ mb: 2 }}>
					Connect an EVM or Solana wallet to see your fees.
				</Muted>
			)}

			{connectedAddress && !activeAuth && (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 3 }}>
					<Muted variant="appSmallRegular" sx={{ textAlign: 'center' }}>
						Sign a message to prove you own this wallet. It is free, does not touch your funds, and is
						never sent on chain.
					</Muted>
					<Button variant="contained" onClick={signIn} disabled={signing}>
						{signing ? 'Waiting for wallet…' : 'Sign in'}
					</Button>
				</Box>
			)}

			{activeAuth && (
				<>
					<SummaryGrid>
						<Stat>
							<Muted variant="appTinyBold">Wallet</Muted>
							<Typography variant="appSmallRegular" sx={{ fontFamily: 'monospace' }}>
								{activeAuth.address.slice(0, 6)}…{activeAuth.address.slice(-4)}
							</Typography>
						</Stat>
						<Stat>
							<Muted variant="appTinyBold">Referrer groups</Muted>
							<Tooltip title="Commitments you are a member of. Fees are split between members at fixed shares.">
								<Typography variant="appSmallRegular">
									{identityLoading ? '…' : (identity?.memberships.length ?? 0)}
								</Typography>
							</Tooltip>
						</Stat>
						<Stat>
							<Muted variant="appTinyBold">Your total share</Muted>
							<Typography variant="appSmallRegular">
								{identityLoading ? '…' : totalBps ? `${totalBps} bps` : '—'}
							</Typography>
						</Stat>
						<Stat>
							<Muted variant="appTinyBold">Direct referrer</Muted>
							<Tooltip title="Whether fees also accrue directly to this address, outside any group.">
								<Typography variant="appSmallRegular">
									{identityLoading ? '…' : identity?.isSingleReferrer ? 'Yes' : 'No'}
								</Typography>
							</Tooltip>
						</Stat>
					</SummaryGrid>

					<Tabs
						value={tab}
						onChange={(_, v) => setTab(v)}
						sx={{ width: 1, mb: 2, minHeight: 36 }}
						variant="fullWidth"
					>
						<Tab value="positions" label="Balances" sx={{ minHeight: 36 }} />
						<Tab value="redeems" label="Paid out" sx={{ minHeight: 36 }} />
						<Tab value="deposits" label="Earned" sx={{ minHeight: 36 }} />
					</Tabs>

					{tab === 'positions' && (
						<FeePositions
							auth={activeAuth}
							pageSize={PAGE_SIZE}
							evmProviders={store?.wallets.evm.staticProviders}
							solanaConnection={solanaConnection}
						/>
					)}
					{tab === 'redeems' && (
						<FeeHistory
							key="redeems"
							emptyLabel="No payouts recorded yet."
							pageSize={PAGE_SIZE}
							fetchPage={(offset) => getRedeems(activeAuth, { limit: PAGE_SIZE, offset })}
						/>
					)}
					{tab === 'deposits' && (
						<FeeHistory
							key="deposits"
							emptyLabel="No fees recorded yet."
							pageSize={PAGE_SIZE}
							fetchPage={(offset) => getDeposits(activeAuth, { limit: PAGE_SIZE, offset })}
						/>
					)}
				</>
			)}
		</Root>
	);
});

export default dynamic(() => Promise.resolve(ReferrerDashboard), { ssr: false });
